import AsyncWrapper from '@/components/AsyncWrapper';
import { CommanderResponse, CommanderResponseRef } from '@/components/Commander';
import LayoutAdmin from '@/components/LayoutAdmin';
import LayoutPos from '@/components/LayoutPOS';
import PosErrorBoundary from '@/components/PosErrorBounary';
import { BaseInfoProvider } from '@/context/BaseInfoContext';
import { BaseInfo } from '@/models/baseInfo';
import { IS_POS, IS_REAL_POS } from '@/utils/env';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import iosWeekday from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { Outlet, useOutletContext } from 'react-router-dom';
import api from './api';
import { APIError } from './api/types';
import './app.less';
import { store } from './redux/store';
import { Brand_Logo } from './utils/uiHelper';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(iosWeekday);
dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

const devHost = IS_REAL_POS ? location.host.replace(/:\d+/, '') : location.host.replace(/:\d+/, '');
export const API = IS_PROD && !IS_TEST ? '' : `//${devHost}:8083`;
export const POS_WS_API = IS_PROD && !IS_TEST ? `wss://${location.hostname}` : `ws://${devHost}:8083`;

export const APIEndpoint = `${API}/admin`;

// 仅 ant-upload 组件使用
export const ImageUploadEndpoint = `${APIEndpoint}/storage/image`;

export const TencentMediaEndpoint = `${APIEndpoint}/tencent/media`;

api.configure({
  msgKey: 'msg',
  beforeSend: req => {
    req.url = `${APIEndpoint}${req.url}`;
    return true;
  },
  headers: IS_POS
    ? {
        'X-Device-Id': (() => window.__POS_Device__.getUUID())(),
      }
    : {},
  rejectAdaptor: err => {
    try {
      if (err.status === 401) {
        // history.push(`/login?return=${encodeURIComponent(location.pathname)}`);
        // setTimeout(() => {
        // location.href = `/login?return=${encodeURIComponent(
        //   `${location.pathname}${location.search}`
        // )}&`;
        if (!IS_POS) {
          location.href = `/login?return=${location.pathname}&${location.search.replace(/^\?/, '')}`;
        }
        // }, 500);
      }
    } catch (e) {}
    return err;
  },
});

type Props = {};

export interface APPState {
  error: APIError;
  loaded: boolean;
  loading: boolean;
  baseInfo: BaseInfo;
}
interface ContextWrapperProp {
  children: React.ReactNode;
  onChange?: ({ error, loaded, loading, baseInfo }: APPState) => void;
}

/**
 * 包裹 BaseInfoContext
 * @param param0
 * @returns
 */
const ContextWrapperInner: React.FC<ContextWrapperProp> = ({ children, onChange }: ContextWrapperProp) => {
  const commanderRef = useRef<CommanderResponseRef>();

  return (
    <BaseInfoProvider onChange={onChange} commanderRef={commanderRef}>
      <>
        <CommanderResponse ref={commanderRef} />
        {children}
      </>
    </BaseInfoProvider>
  );
};

export const ContextWrapper: React.FC<ContextWrapperProp> = ({ children, onChange }: ContextWrapperProp) => {
  return (
    <Provider store={store}>
      <ContextWrapperInner onChange={onChange}>{children}</ContextWrapperInner>
    </Provider>
  );
};

export const App: React.FC<Props> = () => {
  const [appInfo, setState] = useState<APPState>({
    loading: false,
    loaded: false,
  } as APPState);

  useEffect(() => {
    if (appInfo.error && appInfo.error.status === 401) {
      window.location.href = `/login?return=${location.pathname}&${location.search.replace(/^\?/, '')}`;
    }
  }, [appInfo.error]);

  return (
    <ContextWrapper
      onChange={state => {
        setState(state);
      }}>
      <Helmet>
        <title>{appInfo?.baseInfo?.poiName || 'VeeYung'}</title>
        <link rel="icon" href={Brand_Logo} type="image/x-icon" />
      </Helmet>
      <Outlet context={appInfo} />
    </ContextWrapper>
  );
};

type ModuleProps = Props & {
  loading?: boolean;
  loaded?: boolean;
  error?: APIError;
};

export const AppAdmin: React.FC<ModuleProps> = () => {
  const { loaded, loading, error } = useOutletContext<APPState>();
  return (
    <AsyncWrapper isInit loading={loading} error={error} render={loaded}>
      {() => (
        <LayoutAdmin>
          <Outlet />
        </LayoutAdmin>
      )}
    </AsyncWrapper>
  );
};

export const AppPOS: React.FC<ModuleProps> = () => {
  const { loaded, loading, error } = useOutletContext<APPState>();
  return (
    <>
      <Helmet>
        <title>{'Veeyung 商户终端'}</title>
      </Helmet>
      <PosErrorBoundary>
        <AsyncWrapper isInit loading={loading} error={error} render={loaded}>
          {() => (
            <LayoutPos>
              <Outlet />
            </LayoutPos>
          )}
        </AsyncWrapper>
      </PosErrorBoundary>
    </>
  );
};
